import React, { useEffect, useMemo, useState, useRef } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import GoogleSpreadsheetConfig from './IngestionSources/GoogleSpreadsheetConfig';
import GoogleDriveFolderConfig from './IngestionSources/GoogleDriveFolderConfig';
import ApiSourceConfig from './IngestionSources/ApiSourceConfig';
import TransformConfig from './IngestionSources/TransformConfig';
import TransformSqlConfig from './IngestionSources/TransformSqlConfig';
import PostgresDbConfig from './IngestionSources/PostgresDbConfig';
import GroupDedupeConfig from './GroupDedupeConfig';
import ScheduleConfig from './ScheduleConfig';
import GenericDropdown from './GenericDropdown';
import NameSlugFields from './NameSlugFields';
import { slugifyName, getSlugConflictMessage, normalizeSlugInput } from '../utils/slugify';
import { migrateGroupDedupeConfig } from '../utils/groupDedupe';

/** Match DB/API: only explicit false is public; null/undefined/true mean private. */
function isPrivateFromInitial(is_private) {
  return is_private !== false;
}

function blankCreateFormState() {
  return {
    slug: '',
    name: '',
    slugTouched: false,
    type: 'none',
    config: { type: 'none' },
    autoApprove: false,
    autoEmbed: false,
    isPrivate: true,
  };
}

// Reusable editor for ingestion groups (create/edit)
// props:
// - open: bool
// - mode: 'create' | 'edit'
// - initial: { group_slug, name, config }
// - suffix: string (ingestion suffix)
// - onClose()
// - onSaved()
const IngestionGroupEditor = ({ open, mode = 'create', initial = {}, suffix, onClose, onSaved }) => {
  const blank = blankCreateFormState();
  const [slug, setSlug] = useState(initial.group_slug || initial.slug || blank.slug);
  const [name, setName] = useState(initial.name || blank.name);
  const [slugTouched, setSlugTouched] = useState(false);
  const [existingSlugs, setExistingSlugs] = useState([]);
  const [type, setType] = useState((initial?.config?.type) || blank.type);
  const [config, setConfig] = useState(initial.config || { type: blank.type });
  const [autoApprove, setAutoApprove] = useState(initial.auto_approve || false);
  const [showAutoApproveConfirm, setShowAutoApproveConfirm] = useState(false);
  const [autoEmbed, setAutoEmbed] = useState(initial.auto_embed || false);
  const [showAutoEmbedConfirm, setShowAutoEmbedConfirm] = useState(false);
  const [isPrivate, setIsPrivate] = useState(isPrivateFromInitial(initial.is_private));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const openedGroupKeyRef = useRef(null);
  const latestApiConfigRef = useRef(null);

  const applyFormState = (next) => {
    setSlug(next.slug ?? '');
    setName(next.name ?? '');
    setSlugTouched(Boolean(next.slugTouched));
    const t = next.type || 'none';
    setType(t);
    setConfig(migrateGroupDedupeConfig(
      next.config && typeof next.config === 'object' ? next.config : { type: t }
    ));
    setAutoApprove(Boolean(next.autoApprove));
    setAutoEmbed(Boolean(next.autoEmbed));
    setIsPrivate(isPrivateFromInitial(next.isPrivate));
  };

  // When opening the modal (or switching to a different group), load initial form state.
  // Create mode always starts blank — never reuse the previous group's values.
  useEffect(() => {
    if (!open) {
      openedGroupKeyRef.current = null;
      latestApiConfigRef.current = null;
      return;
    }

    const groupKey = `${mode}:${initial.group_slug || initial.slug || 'create'}`;
    if (openedGroupKeyRef.current === groupKey) return;
    openedGroupKeyRef.current = groupKey;

    if (mode === 'create') {
      applyFormState(blankCreateFormState());
    } else {
      applyFormState({
        slug: initial.group_slug || initial.slug || '',
        name: initial.name || '',
        slugTouched: true,
        type: (initial?.config?.type) || 'none',
        config: initial.config || { type: (initial?.config?.type) || 'none' },
        autoApprove: initial.auto_approve || false,
        autoEmbed: initial.auto_embed || false,
        isPrivate: initial.is_private,
      });
    }
    setError('');
    setSubmitting(false);
    setShowAutoApproveConfirm(false);
    setShowAutoEmbedConfirm(false);
  }, [open, mode, initial.group_slug, initial.slug, initial.name, initial.config, initial.auto_approve, initial.auto_embed, initial.is_private]);

  // Drop any legacy create draft left by older builds
  useEffect(() => {
    try {
      sessionStorage.removeItem('cascade_ingestion_create_draft');
    } catch (_) {
      // ignore
    }
  }, []);

  const baseApi = useMemo(() => {
    if (!suffix) return '';
    return `${API_BASE_URL}/casc/v1/api/ingestion/suffixes/${encodeURIComponent(suffix)}/groups`;
  }, [suffix]);

  useEffect(() => {
    if (!open || mode !== 'create' || !baseApi) {
      setExistingSlugs([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const resp = await fetch(baseApi, { headers });
        const json = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const list = json.groups || json.data || json.items || [];
        setExistingSlugs(
          list.map((g) => g.group_slug || g.slug).filter(Boolean)
        );
      } catch (_) {
        if (!cancelled) setExistingSlugs([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, mode, baseApi]);

  const slugConflict = useMemo(() => {
    if (mode === 'edit') return null;
    return getSlugConflictMessage(slug, name, existingSlugs);
  }, [mode, slug, name, existingSlugs]);

  const handleNameChange = (value) => {
    setName(value);
    if (mode === 'create' && !slugTouched) {
      setSlug(slugifyName(value));
    }
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setSlug(normalizeSlugInput(value));
  };

  const canSubmit = useMemo(() => {
    const s = (slug || '').trim();
    const n = (name || '').trim();
    if (!s || !n || slugConflict) return false;
    if (type === 'google_spreadsheet') {
      const sid = (config?.spreadsheet_id || '').trim();
      if (!sid) return false;
    }
    if (type === 'google_drive_folder') {
      const fid = (config?.folder_id || '').trim();
      const flink = (config?.folder_link || '').trim();
      if (!fid && !flink) return false;
    }
    if (type === 'transform') {
      const sources = Array.isArray(config?.sources) ? config.sources : [];
      if (sources.length === 0) return false;
      if (!sources.every((s) => (s?.alias || '').trim() && (s?.group_slug || '').trim())) return false;
    }
    if (type === 'transform_sql') {
      const sources = Array.isArray(config?.sources) ? config.sources : [];
      if (sources.length === 0) return false;
      if (!sources.every((s) => (s?.alias || '').trim() && (s?.group_slug || '').trim())) return false;
      if (!String(config?.sql || '').trim()) return false;
    }
    if (type === 'postgres_db') {
      const pg = config?.postgres || {};
      if (!String(pg.connection_id || '').trim()) return false;
      if (!String(pg.table || '').trim()) return false;
    }
    const dedupe = config?.dedupe || {};
    const dedupeMode = String(dedupe.mode || 'add').toLowerCase();
    if (dedupeMode !== 'add') {
      const hasKey = String(dedupe.unique_key || '').trim() || String(dedupe.unique_key_sql || '').trim();
      if (!hasKey) return false;
    }
    return true;
  }, [slug, name, type, config, slugConflict]);

  const typeOptions = [
    { id: 'none', name: 'None' },
    { id: 'google_spreadsheet', name: 'Google Spreadsheet' },
    { id: 'google_drive_folder', name: 'Google Drive Folder' },
    { id: 'api_source', name: 'API Source' },
    { id: 'postgres_db', name: 'Postgres database' },
    { id: 'transform', name: 'Transformation' },
    { id: 'transform_sql', name: 'SQL Transformation' },
  ];

  const handleAutoApproveToggle = (e) => {
    const newValue = e.target.checked;
    if (newValue && !autoApprove) {
      // Turning ON - show confirmation
      setShowAutoApproveConfirm(true);
    } else {
      // Turning OFF - no confirmation needed
      setAutoApprove(false);
    }
  };

  const confirmAutoApprove = () => {
    setAutoApprove(true);
    setShowAutoApproveConfirm(false);
  };

  const cancelAutoApprove = () => {
    setShowAutoApproveConfirm(false);
  };

  const handleAutoEmbedToggle = (e) => {
    const newValue = e.target.checked;
    if (newValue && !autoEmbed) {
      setShowAutoEmbedConfirm(true);
    } else {
      setAutoEmbed(false);
    }
  };

  const confirmAutoEmbed = () => {
    setAutoEmbed(true);
    setShowAutoEmbedConfirm(false);
  };

  const cancelAutoEmbed = () => {
    setShowAutoEmbedConfirm(false);
  };

  const handleClose = () => {
    // Reset so a quick re-open cannot flash stale values before the open effect runs
    if (mode === 'create') {
      applyFormState(blankCreateFormState());
      openedGroupKeyRef.current = null;
    }
    onClose && onClose();
  };

  const multiSourceWarning = useMemo(() => {
    if (type !== 'transform' && type !== 'transform_sql') return false;
    const sources = Array.isArray(config?.sources) ? config.sources : [];
    return sources.filter((s) => (s?.group_slug || '').trim()).length > 1;
  }, [type, config]);

  const onSubmit = async (e) => {
    e && e.preventDefault();
    if (!canSubmit || !baseApi) return;
    if (slugConflict) {
      setError(slugConflict);
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      // ApiSourceConfig keeps a ref of the latest API blob (debounce). That ref can lag
      // behind group-level fields like dedupe when only Deduplication was edited.
      const apiRefConfig = (type === 'api_source' && latestApiConfigRef.current)
        ? latestApiConfigRef.current
        : null;
      const stateConfig = config && typeof config === 'object' ? config : {};
      const effectiveConfig = {
        ...(apiRefConfig || stateConfig),
        ...stateConfig,
        ...(apiRefConfig?.api_source ? { api_source: apiRefConfig.api_source } : {}),
        type: type || stateConfig.type || apiRefConfig?.type || 'none',
        ...(stateConfig.dedupe !== undefined
          ? { dedupe: stateConfig.dedupe }
          : (apiRefConfig?.dedupe ? { dedupe: apiRefConfig.dedupe } : {})),
      };

      const savedConfig = migrateGroupDedupeConfig({ ...effectiveConfig, type: type || 'none' });
      // Drop legacy per-source dedupe fields once migrated onto config.dedupe
      delete savedConfig.mode;
      delete savedConfig.output;
      if (savedConfig.api_source && typeof savedConfig.api_source === 'object') {
        const api = { ...savedConfig.api_source };
        delete api.unique_key;
        delete api.unique_key_sql;
        savedConfig.api_source = api;
      }

      // Build payloads per mode to avoid sending immutable fields during edit
      const payloadCreate = {
        groupSlug: slug.trim(),
        name: name.trim(),
        config: savedConfig,
        auto_approve: autoApprove,
        auto_embed: autoEmbed,
        is_private: isPrivate,
      };
      const payloadEdit = {
        // Do NOT include groupSlug on PATCH; server forbids changing slug
        name: name.trim(),
        config: savedConfig,
        auto_approve: autoApprove,
        auto_embed: autoEmbed,
        is_private: isPrivate,
      };
      let resp = null;
      const headers = await getAuthHeaders();
      const groupSlugToUse = mode === 'edit' ? (initial.group_slug || initial.slug || slug) : slug.trim();
      
      if (mode === 'edit') {
        // Prefer PATCH endpoint if available
        const target = `${baseApi}/${encodeURIComponent(groupSlugToUse)}`;
        resp = await fetch(target, { method: 'PATCH', headers, body: JSON.stringify(payloadEdit) });
        if (!resp.ok && resp.status === 404) {
          // Fallback to create if update not supported
          resp = await fetch(baseApi, { method: 'POST', headers, body: JSON.stringify(payloadCreate) });
        }
      } else {
        resp = await fetch(baseApi, { method: 'POST', headers, body: JSON.stringify(payloadCreate) });
      }
      const json = await resp.json().catch(() => ({}));
      if (!resp.ok || json?.success === false) {
        const msg = json?.message || json?.error || 'Failed to save ingestion group';
        throw new Error(msg);
      }
      
      if (mode === 'create') {
        applyFormState(blankCreateFormState());
        openedGroupKeyRef.current = null;
      }
      onSaved && onSaved();
      onClose && onClose();
    } catch (e2) {
      setError(e2.message || 'Failed to save');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className={`modal show`} role="dialog" aria-modal="true">
      <div className="modal-content wide ingestion-group-editor-modal">
        <div className="modal-header">
          <h2>{mode === 'edit' ? 'Edit Ingestion Group' : 'New Ingestion Group'}</h2>
          <button className="close" onClick={handleClose} aria-label="Close">
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className="modal-body">
          {error && (
            <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i> {error}</div>
          )}
          <form onSubmit={onSubmit}>
            <NameSlugFields
              className="form-row"
              name={name}
              slug={slug}
              onNameChange={handleNameChange}
              onSlugChange={handleSlugChange}
              slugConflict={slugConflict}
              isEdit={mode === 'edit'}
              nameId="ingestion-group-name"
              slugId="ingestion-group-slug"
              slugPlaceholder="group-slug"
            />
            <div className="form-row">
              <label>Source Type</label>
              <GenericDropdown
                items={typeOptions}
                value={type || 'none'}
                onChange={(id) => { const t = id || 'none'; setType(t); setConfig(prev => ({ ...(prev||{}), type: t })); }}
                placeholder="Select source type"
              />
            </div>
            {type === 'google_spreadsheet' && (
              <GoogleSpreadsheetConfig value={config || {}} onChange={setConfig} />
            )}
            {type === 'google_drive_folder' && (
              <GoogleDriveFolderConfig value={config || {}} onChange={setConfig} />
            )}
            {type === 'api_source' && (
              <ApiSourceConfig
                key={`${mode}-${initial.group_slug || initial.slug || 'create'}`}
                value={config || {}}
                onChange={setConfig}
                latestConfigRef={latestApiConfigRef}
              />
            )}
            {type === 'postgres_db' && (
              <PostgresDbConfig
                key={`${mode}-${initial.group_slug || initial.slug || 'create'}-postgres`}
                value={config || { type: 'postgres_db', postgres: {} }}
                onChange={setConfig}
                suffix={suffix}
              />
            )}
            {type === 'transform' && (
              <TransformConfig
                key={`${mode}-${initial.group_slug || initial.slug || 'create'}-transform`}
                value={config || { type: 'transform', sources: [], steps: [] }}
                onChange={setConfig}
                suffix={suffix}
                groupSlug={mode === 'edit' ? (initial.group_slug || initial.slug || slug) : null}
                excludeSlug={slug}
              />
            )}
            {type === 'transform_sql' && (
              <TransformSqlConfig
                key={`${mode}-${initial.group_slug || initial.slug || 'create'}-transform-sql`}
                value={config || { type: 'transform_sql', sources: [], sql: '' }}
                onChange={setConfig}
                suffix={suffix}
                groupSlug={mode === 'edit' ? (initial.group_slug || initial.slug || slug) : null}
                excludeSlug={slug}
              />
            )}
            {(!type || type === 'none') && (
              <div className="form-row">
                <div className="hint">No additional configuration required.</div>
              </div>
            )}

            <GroupDedupeConfig
              value={config?.dedupe || { mode: 'add' }}
              onChange={(dedupe) => setConfig((prev) => ({ ...(prev || {}), dedupe }))}
              multiSourceWarning={multiSourceWarning}
            />
            
            <div className="form-row">
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: autoApprove ? 'rgba(255, 152, 0, 0.1)' : 'transparent', borderRadius: '6px', border: autoApprove ? '1px solid rgba(255, 152, 0, 0.3)' : '1px solid transparent' }}>
                <input 
                  type="checkbox" 
                  checked={autoApprove} 
                  onChange={handleAutoApproveToggle}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span style={{ flex: 1 }}>
                  <strong>Auto-approve on ingestion</strong>
                  <div className="hint" style={{ marginTop: '4px' }}>
                    {autoApprove ? (
                      <span style={{ color: 'rgba(255, 152, 0, 1)' }}>
                        <i className="fas fa-exclamation-triangle"></i> All ingested entries will automatically be approved and visible to your models
                      </span>
                    ) : (
                      'When enabled, ingested entries skip data review and are immediately approved'
                    )}
                  </div>
                </span>
              </label>
            </div>

            <div className="form-row">
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: autoEmbed ? 'rgba(33, 150, 243, 0.1)' : 'transparent', borderRadius: '6px', border: autoEmbed ? '1px solid rgba(33, 150, 243, 0.3)' : '1px solid transparent' }}>
                <input 
                  type="checkbox" 
                  checked={autoEmbed} 
                  onChange={handleAutoEmbedToggle}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span style={{ flex: 1 }}>
                  <strong>Auto-embed on ingestion</strong>
                  <div className="hint" style={{ marginTop: '4px' }}>
                    {autoEmbed ? (
                      <span style={{ color: 'rgba(33, 150, 243, 1)' }}>
                        <i className="fas fa-vector-square"></i> Embeddings will be automatically triggered after successful ingestion
                      </span>
                    ) : (
                      'When enabled, successful ingestion automatically triggers embedding for configured tools'
                    )}
                  </div>
                </span>
              </label>
            </div>

            {/* ── Periodic Ingestion Schedule ── */}
            {mode === 'edit' && suffix && (initial.group_slug || initial.slug) && (
              <div className="form-row">
                <label style={{ marginBottom: '4px' }}>
                  <i className="fas fa-clock"></i> Periodic Ingestion Schedule{' '}
                  <span style={{ fontWeight: 500, opacity: 0.85 }}>(deprecated)</span>
                </label>
                <div className="hint" style={{ marginBottom: 8 }}>
                  Still works and remains editable. Prefer Settings → Scheduler for new recurring toolchains; this path will be obsolete soon.
                </div>
                <ScheduleConfig
                  suffix={suffix}
                  groupSlug={initial.group_slug || initial.slug}
                  compact={true}
                />
              </div>
            )}

            <div className="form-row">
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', padding: '12px', background: isPrivate ? 'rgba(156, 39, 176, 0.08)' : 'transparent', borderRadius: '6px', border: isPrivate ? '1px solid rgba(156, 39, 176, 0.25)' : '1px solid transparent' }}>
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={e => setIsPrivate(e.target.checked)}
                  style={{ width: 'auto', cursor: 'pointer' }}
                />
                <span style={{ flex: 1 }}>
                  <strong>Private</strong>
                  <div className="hint" style={{ marginTop: '4px' }}>
                    {isPrivate ? (
                      <span style={{ color: 'rgba(156, 39, 176, 0.85)' }}>
                        <i className="fas fa-lock"></i> Only you and admins can see this source
                      </span>
                    ) : (
                      <span><i className="fas fa-lock-open"></i> Visible to all users with access to this horizon</span>
                    )}
                  </div>
                </span>
              </label>
            </div>

            <div className="form-row">
              <div className="hint" style={{ padding: '12px', background: 'rgba(33, 150, 243, 0.1)', borderRadius: '6px', border: '1px solid rgba(33, 150, 243, 0.3)' }}>
                <i className="fas fa-info-circle"></i> Use the "Assign" button on the main page to manage horizon assignments after saving.
                {mode === 'create' && ' Periodic scheduling can be configured after the group is created (via Edit).'}
              </div>
            </div>
          </form>
          
          {/* Auto-approve confirmation dialog */}
          {showAutoApproveConfirm && (
            <div className="confirmation-overlay">
              <div className="confirmation-dialog">
                <div className="confirmation-header">
                  <i className="fas fa-exclamation-triangle" style={{ color: '#ff9800', fontSize: '2em' }}></i>
                  <h3>Enable Auto-Approve?</h3>
                </div>
                <div className="confirmation-body">
                  <p>Are you sure you want to enable auto-approve?</p>
                  <p style={{ marginTop: '12px', fontWeight: 'bold' }}>
                    This means you are automatically making everything ingested visible to your models without review.
                  </p>
                  <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>
                    All new entries will bypass the data review workflow and be immediately marked as approved.
                  </p>
                </div>
                <div className="confirmation-footer">
                  <button className="btn btn-secondary" onClick={cancelAutoApprove}>
                    Do Not Allow
                  </button>
                  <button className="btn btn-warning" onClick={confirmAutoApprove}>
                    <i className="fas fa-check"></i> Allow
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Auto-embed confirmation dialog */}
          {showAutoEmbedConfirm && (
            <div className="confirmation-overlay">
              <div className="confirmation-dialog">
                <div className="confirmation-header">
                  <i className="fas fa-vector-square" style={{ color: '#2196f3', fontSize: '2em' }}></i>
                  <h3>Enable Auto-Embed?</h3>
                </div>
                <div className="confirmation-body">
                  <p>Are you sure you want to enable auto-embed?</p>
                  <p style={{ marginTop: '12px', fontWeight: 'bold' }}>
                    After each successful ingestion, embedding requests will be automatically created for all configured vector search tools.
                  </p>
                  <p style={{ marginTop: '12px', color: 'var(--text-secondary)' }}>
                    This consumes embedding API credits. Failed embeddings will appear in the notification badge and can be cleared individually.
                  </p>
                </div>
                <div className="confirmation-footer">
                  <button className="btn btn-secondary" onClick={cancelAutoEmbed}>
                    Do Not Allow
                  </button>
                  <button className="btn btn-primary" onClick={confirmAutoEmbed}>
                    <i className="fas fa-check"></i> Allow
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={handleClose} disabled={submitting}>Cancel</button>
          <button className="btn btn-primary" onClick={onSubmit} disabled={!canSubmit || submitting}>
            {submitting ? (<><i className="fas fa-spinner fa-spin"></i> Saving…</>) : 'Save'}
          </button>
        </div>
      </div>
      <style>{`
        .modal-content.wide { max-width: min(1100px, 96vw); width: 96%; }
        .ingestion-group-editor-modal .modal-body {
          max-height: calc(100vh - 11rem);
          overflow-y: auto;
          padding-right: 4px;
        }
        .ingestion-group-editor-modal .api-source-config .http-method-options {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
        }
        .ingestion-group-editor-modal .api-source-config .http-method-option {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 14px;
          border-radius: 8px;
          border: 1px solid var(--glass-border, #ddd);
          background: var(--glass-bg, #fff);
          cursor: pointer;
          font-weight: 500;
        }
        .ingestion-group-editor-modal .api-source-config .http-method-option input {
          width: auto;
          margin: 0;
          cursor: pointer;
        }
        .ingestion-group-editor-modal .api-source-config .http-method-option.is-selected {
          border-color: rgba(59, 130, 246, 0.65);
          background: rgba(59, 130, 246, 0.08);
        }
        .modal .form-row { margin-bottom: 16px; display: flex; flex-direction: column; align-items: stretch; gap: 8px; }
        .modal .form-row label { display: block; color: var(--text-secondary); font-weight: 600; font-size: 1.05em; }
        .modal .form-row input[type="text"], .modal .form-row select { width: 100%; padding: 10px; border-radius: 6px; border: 1px solid var(--glass-border,#ddd); background: var(--glass-bg,#fff); color: var(--text-primary,#111); }
        .dark-mode .modal .form-row input[type="text"],
        .dark-mode .modal .form-row select,
        .dark-mode .modal .form-row textarea,
        .dark-mode .ingestion-group-editor-modal .form-control {
          background: #0f172a;
          color: #f1f5f9;
          border-color: #475569;
        }
        .dark-mode .modal .form-row input[type="text"]::placeholder,
        .dark-mode .modal .form-row textarea::placeholder {
          color: #64748b;
        }
        .dark-mode .ingestion-group-editor-modal .api-source-config .http-method-option {
          background: #0f172a;
          border-color: #475569;
          color: #f1f5f9;
        }
        .dark-mode .ingestion-group-editor-modal .api-source-config .http-method-option.is-selected {
          background: rgba(59, 130, 246, 0.18);
          border-color: rgba(59, 130, 246, 0.65);
        }
        /* Make GenericDropdown span full width inside this modal */
        .modal .generic-dropdown .input-wrapper { width: 100%; }
        .modal .generic-dropdown input { width: 100%; }
        .modal .hint { color: var(--text-muted); font-size: 0.9em; margin-top: 4px; }
        
        /* Confirmation dialog styles */
        .confirmation-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10001;
          animation: fadeIn 0.2s;
        }
        .confirmation-dialog {
          background: var(--surface-color, #fff);
          border-radius: 12px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          animation: slideUp 0.3s;
        }
        .confirmation-header {
          display: flex;
          align-items: center;
          gap: 16px;
          margin-bottom: 20px;
        }
        .confirmation-header h3 {
          margin: 0;
          color: var(--text-primary, #111);
          font-size: 1.4em;
        }
        .confirmation-body {
          margin-bottom: 24px;
          line-height: 1.6;
          color: var(--text-primary, #111);
        }
        .confirmation-body p {
          margin: 0;
          color: var(--text-primary, #111);
        }
        
        /* Dark mode specific overrides */
        :root.dark-mode .confirmation-dialog,
        .dark-mode .confirmation-dialog {
          background: #1e1e1e;
        }
        :root.dark-mode .confirmation-header h3,
        .dark-mode .confirmation-header h3 {
          color: #ffffff;
        }
        :root.dark-mode .confirmation-body,
        .dark-mode .confirmation-body,
        :root.dark-mode .confirmation-body p,
        .dark-mode .confirmation-body p {
          color: #ffffff;
        }
        .confirmation-footer {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
        }
        .btn-warning {
          background: #ff9800;
          color: white;
          border: none;
        }
        .btn-warning:hover {
          background: #f57c00;
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default IngestionGroupEditor;
